import React, { useState } from 'react';
import { X, Star, MessageCircle, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

// Use relative URL for same-origin API calls
const API = '/api';

// Different prompts for different days
const FEEDBACK_PROMPTS = [
  {
    day: 3,
    title: "First Impressions",
    question: "How's your experience with VowFlow so far?",
    type: "rating_comment",
    placeholder: "What's working well? What's confusing?"
  },
  {
    day: 6,
    title: "Feature Check-in",
    question: "Which features have you found most useful?",
    type: "open_text",
    placeholder: "Timeline, budget planner, guest list, Grace assistant, etc."
  },
  {
    day: 9,
    title: "Quick Rating",
    question: "Rate your overall experience so far",
    type: "rating_only"
  },
  {
    day: 12,
    title: "Missing Features",
    question: "What features are you wishing VowFlow had?",
    type: "open_text",
    placeholder: "Be specific - this helps us prioritize development"
  },
  {
    day: 15,
    title: "Halfway Check",
    question: "Would you recommend VowFlow to a colleague?",
    type: "thumbs_comment",
    placeholder: "Why or why not?"
  },
  {
    day: 18,
    title: "Ease of Use",
    question: "How easy has VowFlow been to navigate?",
    type: "rating_comment",
    placeholder: "Any areas that are confusing or hard to use?"
  },
  {
    day: 21,
    title: "Value Assessment",
    question: "Do you see value in VowFlow for your work?",
    type: "thumbs_comment",
    placeholder: "What makes it valuable or not valuable?"
  },
  {
    day: 24,
    title: "Feature Deep Dive",
    question: "Which feature surprised you the most (good or bad)?",
    type: "open_text",
    placeholder: "Tell us about a specific feature"
  },
  {
    day: 27,
    title: "Final Thoughts Preview",
    question: "As your trial nears completion, how do you feel about VowFlow?",
    type: "rating_comment",
    placeholder: "Would you consider purchasing it?"
  }
];

const QuickFeedbackPrompt = ({ isOpen, onClose, user, dayNumber }) => {
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [thumbs, setThumbs] = useState(null); // 'up' or 'down'
  const [comment, setComment] = useState('');

  // Find the appropriate prompt based on day
  const prompt = FEEDBACK_PROMPTS.find(p => p.day === dayNumber) || FEEDBACK_PROMPTS[0];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation based on prompt type
    if (prompt.type === 'rating_only' && rating === 0) {
      toast.error('Please provide a rating');
      return;
    }
    if (prompt.type === 'rating_comment' && rating === 0) {
      toast.error('Please provide a rating');
      return;
    }
    if (prompt.type === 'thumbs_comment' && thumbs === null) {
      toast.error('Please select thumbs up or down');
      return;
    }
    if (prompt.type === 'open_text' && !comment.trim()) {
      toast.error('Please share your thoughts');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/feedback/quick-submit`, {
        user_id: user.id,
        day_number: dayNumber,
        prompt_title: prompt.title,
        prompt_question: prompt.question,
        rating: rating || null,
        thumbs: thumbs,
        comment: comment || null
      });

      toast.success('Thanks for your feedback! 🙏');
      onClose(true); // Pass true to indicate feedback was submitted
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      // Record that they saw but skipped this prompt
      await axios.post(`${API}/feedback/skip-prompt`, {
        user_id: user.id,
        day_number: dayNumber
      });
      onClose(false);
    } catch (error) {
      onClose(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200 bg-gradient-to-r from-amber-500 to-orange-500">
          <div className="flex items-center gap-3">
            <MessageCircle className="text-white" size={24} />
            <div>
              <h2 className="text-xl font-serif font-bold text-white">{prompt.title}</h2>
              <p className="text-xs text-white/80">Day {dayNumber} Check-in</p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <p className="text-[#1C1917] font-medium mb-4">{prompt.question}</p>

            {/* Rating Input */}
            {(prompt.type === 'rating_only' || prompt.type === 'rating_comment') && (
              <div className="flex gap-2 justify-center mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      size={36}
                      className={star <= rating ? 'fill-amber-500 text-amber-500' : 'text-stone-300'}
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Thumbs Input */}
            {prompt.type === 'thumbs_comment' && (
              <div className="flex gap-4 justify-center mb-4">
                <button
                  type="button"
                  onClick={() => setThumbs('up')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    thumbs === 'up'
                      ? 'border-green-500 bg-green-50'
                      : 'border-stone-200 hover:border-green-300'
                  }`}
                >
                  <ThumbsUp
                    size={32}
                    className={thumbs === 'up' ? 'text-green-600' : 'text-stone-400'}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => setThumbs('down')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    thumbs === 'down'
                      ? 'border-red-500 bg-red-50'
                      : 'border-stone-200 hover:border-red-300'
                  }`}
                >
                  <ThumbsDown
                    size={32}
                    className={thumbs === 'down' ? 'text-red-600' : 'text-stone-400'}
                  />
                </button>
              </div>
            )}

            {/* Comment Input */}
            {(prompt.type === 'rating_comment' || prompt.type === 'thumbs_comment' || prompt.type === 'open_text') && (
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={prompt.placeholder}
                rows={4}
                className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                required={prompt.type === 'open_text'}
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={handleSkip}
              className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700"
            >
              Skip for Now
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickFeedbackPrompt;
